<script setup lang="ts">
import type { IInvoiceContainer } from '@/types/rc/invoices'
import { PartType, PartTypesShortNames } from '@/types/rc/miners/PartType'
import { formatCurrency } from '@/util/formatters'
import { Icon } from '@iconify/vue'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

defineProps<{ container: IInvoiceContainer }>()
const emit = defineEmits(['back'])

const activeContainers = ref([])

const handleMinerClick = (minerId: number) => {
  const route = router.resolve({ name: 'rc-miner-repairs', params: { minerId: minerId } })
  window.open(route.href, '_blank')
}
</script>

<template>
  <el-card shadow="never">
    <header class="flex items-center h-12 pb-[var(--el-card-padding)] justify-between">
      <div class="flex items-center">
        <Icon class="cursor-pointer w-12 pr-6" icon="ep:arrow-left-bold" @click="emit('back')" />
        <span class="text-text-tertiary">Container &nbsp;</span>
        <span class="font-bold">{{ container.number }}</span>
      </div>
      <div class="flex gap-54 mr-54">
        <h4 class="text-sm">
          {{ container.total_miners }}
          <span class="text-tertiary"> &nbsp; {{ container.total_miners == 1 ? 'miner' : 'miners' }} </span>
        </h4>
        <h4 class="text-sm">
          <span class="text-tertiary">Total:&nbsp; </span> {{ formatCurrency(container.total, 2, true) }}
        </h4>
      </div>
    </header>

    <hr class="border-neutral-700" />
    <div class="grid" style="grid-template-columns: 1fr 10fr">
      <template v-for="(miner, index) in container.miners" :key="miner.sn">
        <el-button link @click="handleMinerClick(miner.id)">
          <div class="my-spacing-md py-[12px] text-tertiary text-sm">Miner {{ index + 1 }}</div>
        </el-button>
        <el-collapse v-model="activeContainers">
          <el-collapse-item>
            <template #title>
              <div class="flex items-center">
                <p class="w-[310px] flex items-center gap-2">
                  <Icon icon="mdi:triangle-small-down" />{{ miner.model }}
                </p>
                <p class="w-[340px] text-left font-light">{{ miner.sn }}</p>
                <p class="w-[80px] text-right">
                  <span class="text-tertiary">$</span>
                  {{ formatCurrency(miner.total, 2, false) }}
                </p>
              </div>
            </template>
            <el-table :data="miner.fees" :show-header="false">
              <el-table-column :width="300">
                <template #default="{ row }">
                  <p class="text-tertiary">{{ PartTypesShortNames[row.part_type as PartType] }}</p>
                </template>
              </el-table-column>
              <el-table-column :width="200">
                <template #default="{ row }">
                  <div>
                    <p>{{ row.action }}</p>
                    <p v-if="row.failure_type" class="text-tertiary text-xs">
                      {{ row.failure_type == 'Other' && row.comment ? row.comment : row.failure_type }}
                    </p>
                  </div>
                </template>
              </el-table-column>
              <el-table-column :width="100">
                <template #default="{ row }">
                  <p class="text-right">
                    <span class="text-tertiary">$</span>
                    {{ formatCurrency(row.price, 2, false) }}
                  </p>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </template>
    </div>
  </el-card>
</template>

<style scoped lang="scss">
:deep() {
  .el-table {
    width: max-content;

    tr {
      background: transparent;
    }
  }

  td.el-table__cell {
    border: none;
  }

  .el-collapse {
    border: none;
  }

  .el-collapse-item {
    &__header {
      width: max-content;
      border-bottom: 1px solid var(--fill-tertiary);

      .iconify {
        transition: 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19);
        transform: rotate(0deg);
      }

      &.is-active .iconify {
        transform: rotate(-90deg);
      }
    }

    &__arrow {
      display: none;
    }

    &__wrap {
      border: none;
    }
  }
}
</style>
